import { commonClientResx } from "~/resxDist/commonClientResx";
import { isNegative, round } from "~/utils/math";
import { currentCultureSettings } from "../setCulture";

ep.common = ep.common || ({} as EPCommon);

interface TimeObj {
    hour: number;
    minute: number;
    second: number;
    millisec?: number;
    microsec?: number;
    timezone?: any;
}

export class TimeUtility {
    private _defaults: any;
    private static _instance: TimeUtility;
    static new(): TimeUtility {
        return this._instance ? this._instance : new TimeUtility();
    }

    convert24to12(hour: number): string {
        hour %= 12;
        if (hour === 0) {
            hour = 12;
        }
        return String(hour);
    }
    formatTime(format: string, time: TimeObj, options?: { amNames?: string; pmNames?: string }): string {
        const _this = this;

        _this._defaults = {
            amNames: ["AM", "A"],
            pmNames: ["PM", "P"],
        };

        options = {
            ..._this._defaults,
            ...options,
        };

        time = {
            hour: 0,
            minute: 0,
            second: 0,
            millisec: 0,
            microsec: 0,
            timezone: null,
            ...time,
        };

        let tmptime = format,
            ampmName = options.amNames[0];
        const hour = time.hour;
        if (hour > 11) {
            ampmName = options.pmNames[0];
        }

        tmptime = tmptime.replace(/(?:HH?|hh?|mm?|ss?|[tT]{1,2}|[zZ]|[lc]|'.*?')/g, (match: any) => {
            switch (match) {
                case "HH":
                    return ("0" + hour).slice(-2);
                case "H":
                    return hour;
                case "hh":
                    return ("0" + _this.convert24to12(hour)).slice(-2);
                case "h":
                    return _this.convert24to12(hour);
                case "mm":
                    return ("0" + time.minute).slice(-2);
                case "m":
                    return time.minute;
                case "ss":
                    return ("0" + time.second).slice(-2);
                case "s":
                    return time.second;
                case "l":
                    return ("00" + time.millisec).slice(-3);
                case "c":
                    return ("00" + time.microsec).slice(-3);
                case "T":
                    return ampmName.charAt(0).toUpperCase();
                case "TT":
                    return ampmName.toUpperCase();
                case "t":
                    return ampmName.charAt(0).toLowerCase();
                case "tt":
                    return ampmName.toLowerCase();
                default:
                    return match.replace(/'/g, "");
            }
        });

        return tmptime;
    }

    getTimeObjFromDate(date: Date): TimeObj {
        return {
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds(),
        };
    }

    formatDateTime(date: Date, format: string): string {
        const time = this.getTimeObjFromDate(date);
        return this.formatTime(format, time);
    }

    formatAsShortTime(date: Date): string {
        return this.formatDateTime(date, currentCultureSettings.shortTimeFormat);
    }

    formatAsLongTimeFormat(date: Date): string {
        return this.formatDateTime(date, currentCultureSettings.LongTimeFormat);
    }

    toUserTime(date: Date): Date {
        if (!date) {
            return date;
        }
        const resultDate = new Date(date);
        resultDate.setMinutes(date.getMinutes() - this.getTimeZoneOffset());
        return resultDate;
    }

    getUTCFromUserTime(userDate: Date): Date {
        if (!userDate) {
            return userDate;
        }
        const resultDate = new Date(userDate);
        resultDate.setMinutes(userDate.getMinutes() + this.getTimeZoneOffset());
        return resultDate;
    }

    getTimeZoneOffset(): number {
        return new Date().getTimezoneOffset();
    }

    /**
     * return hours and minutes
     * @param value Decimal value of Hours
     */
    getHourAndMinutesFromHours(value: number = 0): { hours: number; minutes: number } {
        const hours = Math.floor(value) || 0;
        const minutes = Math.round((value - hours) * 60);

        return { hours, minutes };
    }

    /**
     * return hours and minutes
     * @param value Decimal value of Minutes
     */
    getHourAndMinutesFromMinutes(value: number = 0): { hours: number; minutes: number } {
        const hours = Math.floor(value / 60) || 0;
        const minutes = Math.round(value - hours * 60);

        return { hours, minutes };
    }

    /**
     * Formats in 1h 20m format
     * @param value Decimal value of Hours
     */
    formatAsAbbrFromHours(value: number = 0): string {
        if (!value) {
            return `0${commonClientResx.HourAbbreviation}`;
        }

        const { hours, minutes } = this.getHourAndMinutesFromHours(value);

        return formatAsAbbr(hours, minutes);
    }

    /**
     * Formats in 1h 20m format
     * @param value Integer value of Minutes
     */
    formatAsAbbrFromMinutes(value: number = 0): string {
        if (!value) {
            return `0${commonClientResx.HourAbbreviation}`;
        }

        const sign = isNegative(value) ? "-" : "";
        value = Math.abs(value);

        const { hours, minutes } = this.getHourAndMinutesFromMinutes(value);

        return `${sign}${formatAsAbbr(hours, minutes)}`;
    }

    /**
     * Formats in 1h 20m format
     * @param hours Decimal value of Hours
     * @param minutes Decimal value of Minutes
     * @param isEmpty Boolean value when need return empty string without 0h
     */
    formatAsAbbrFromHoursAndMinutes(hours: number = 0, minutes: number = 0, isEmpty: boolean = false): string {
        if (!hours && !minutes) {
            if (isEmpty) {
                return "";
            }

            return `0${commonClientResx.HourAbbreviation}`;
        }

        return formatAsAbbr(hours, minutes);
    }

    formatFromMinutes(value: number = 0): string {
        const hours = value / 60;
        const rhours = Math.floor(hours);

        const minutes = (hours - rhours) * 60;
        const rminutes = Math.round(minutes);

        const withLeadingZeroMinutes = leadingZeroFunc(rminutes);

        return `${rhours}:${withLeadingZeroMinutes}${rminutes}`;
    }

    formatToMinutes(value: string = ""): number {
        const partsOfStr = value.split(":");

        const hours = parseInt(partsOfStr[0], 10) || 0;
        const minutes = parseInt(partsOfStr[1], 10) || 0;

        return hours * 60 + minutes;
    }

    parseHoursAndMinutes(
        value: string = "",
        treatZeroAsEmpty: boolean = false
    ): { hours: number; minutes: number; isEmpty: boolean } {
        const partsOfStr = value.split(":");

        const hours = parseInt(partsOfStr[0], 10) || 0;
        const minutes = parseInt(partsOfStr[1], 10) || 0;
        const isEmpty = treatZeroAsEmpty && !hours && !minutes;

        return { hours, minutes, isEmpty };
    }

    convertToHours(hours: number = 0, minutes: number = 0): number {
        return hours + round(minutes ? minutes / 60 : 0, 2);
    }

    convertToMinutes(hours: number = 0, minutes: number = 0): number {
        return round(hours * 60 + minutes, 2);
    }
}

const leadingZeroFunc = (time: number) => (time < 10 ? "0" : "");

const formatAsAbbr = (hours: number, minutes: number) => {
    const hoursResult = !!hours ? hours + commonClientResx.HourAbbreviation : "";
    const minutesResult = !!minutes ? minutes + commonClientResx.MinuteAbbreviation : "";
    const space = hoursResult && minutesResult && " ";

    return `${hoursResult}${space}${minutesResult}`;
};

export const timeUtility: TimeUtility = TimeUtility.new();
